import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Chip,
  CircularProgress,
  Button,
  Alert,
  Checkbox
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { apiService } from '../services/api';

const DeleteExperts = () => {
  const [experts, setExperts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedExperts, setSelectedExperts] = useState([]);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [deletingExpertId, setDeletingExpertId] = useState(null);
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    const isAuth = apiService.isAuthenticated();
    if (!isAuth) {
      setIsAuthorized(false);
      setLoading(false);
      return;
    }

    const adminUser = apiService.getAdminUser();
    if (adminUser && adminUser.id) {
      setIsAuthorized(true);
      fetchAllExperts();
    } else {
      setIsAuthorized(false);
      setLoading(false);
    }
  }, []);

  const fetchAllExperts = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        'https://bisauli.com/api3/controllers/ExpertController.php?action=getAllExperts'
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success && data.data) {
        setExperts(data.data);
        
        if (data.data.length === 0) {
          setAlert({
            severity: 'info',
            message: 'No experts found'
          });
        }
      } else {
        throw new Error(data.message || 'Failed to fetch experts');
      }
    } catch (error) {
      console.error('Error fetching experts:', error);
      setAlert({
        severity: 'error',
        message: 'Failed to load experts: ' + error.message
      });
    } finally {
      setLoading(false);
    }
  };

  // ✅ Select/Deselect single expert
  const handleSelectExpert = (expertId) => {
    setSelectedExperts((prev) =>
      prev.includes(expertId)
        ? prev.filter((id) => id !== expertId)
        : [...prev, expertId]
    );
  };

  // ✅ Select/Deselect all experts
  const handleSelectAll = () => {
    if (selectedExperts.length === experts.length) {
      setSelectedExperts([]);
    } else {
      setSelectedExperts(experts.map((expert) => expert.id));
    }
  };

  // ✅ Handle single delete
  const handleDeleteClick = (expert) => {
    setDeletingExpertId(expert.id);
    setDeleteDialogOpen(true);
  };

  // ✅ Handle batch delete
  const handleBatchDelete = () => {
    if (selectedExperts.length === 0) {
      setAlert({
        severity: 'warning',
        message: 'Please select at least one expert to delete'
      });
      return;
    }
    setDeleteDialogOpen(true);
  };

  // ✅ Confirm delete (single or batch)
  const confirmDelete = async () => {
    try {
      setLoading(true);
      const idsToDelete = deletingExpertId ? [deletingExpertId] : selectedExperts;
      let successCount = 0;
      let failCount = 0;

      for (const expertId of idsToDelete) {
        try {
          const response = await fetch(
            `https://bisauli.com/api3/controllers/ExpertController.php?action=deleteExpert&id=${expertId}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
              },
            }
          );

          if (!response.ok) {
            failCount++;
            continue;
          }

          const result = await response.json();
          if (result.success) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (error) {
          console.error('Failed to delete expert:', expertId, error);
          failCount++;
        }
      }

      // ✅ Show result
      setAlert({
        severity: failCount > 0 ? 'warning' : 'success',
        message: `Deleted: ${successCount}${failCount > 0 ? `, Failed: ${failCount}` : ''}`
      });

      setDeleteDialogOpen(false);
      setDeletingExpertId(null);
      setSelectedExperts([]);
      
      // ✅ Refresh the list
      await fetchAllExperts();
    } catch (error) {
      console.error('Error deleting experts:', error);
      setAlert({
        severity: 'error',
        message: 'Failed to delete expert(s): ' + error.message
      });
    } finally {
      setLoading(false);
    }
  };

  // ✅ Authorization check
  if (!isAuthorized) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center', 
        py: 6,
        textAlign: 'center'
      }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          🔒 Access Denied
        </Typography>
        <Typography variant="body1" color="text.secondary">
          You must be logged in as an admin to view this section.
        </Typography>
      </Box>
    );
  }

  // ✅ Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // ✅ Empty state
  if (experts.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="text.secondary">No experts found</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* ✅ ALERTS */}
      {alert && (
        <Alert 
          severity={alert.severity} 
          onClose={() => setAlert(null)}
          sx={{ mb: 3 }}
        >
          {alert.message}
        </Alert>
      )}

      {/* ✅ WARNING BANNER */}
      <Alert severity="error" sx={{ mb: 3 }}>
        ⚠️ <strong>Warning!</strong> Deleted experts will be moved to trash . 
       
      </Alert>

      {/* ✅ SELECT/DELETE CONTROLS */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <Button
          variant="outlined"
          onClick={handleSelectAll}
          sx={{ transition: 'all 0.3s', '&:hover': { transform: 'translateY(-2px)' } }}
        >
          {selectedExperts.length === experts.length ? 'Deselect All' : 'Select All'}
        </Button>
        <Typography variant="body2" color="text.secondary">
          {selectedExperts.length} selected of {experts.length}
        </Typography>
        {selectedExperts.length > 0 && (
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleBatchDelete}
            disabled={loading}
            sx={{ transition: 'all 0.3s', '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 } }}
          >
            Delete Selected ({selectedExperts.length})
          </Button>
        )}
      </Box>

      {/* ✅ TABLE WITH CHECKBOXES */}
      <TableContainer component={Paper} sx={{ boxShadow: 2, borderRadius: 2 }}>
        <Table>
          <TableHead sx={{ bgcolor: '#fff3e0' }}>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedExperts.length > 0 && selectedExperts.length < experts.length}
                  checked={selectedExperts.length === experts.length && experts.length > 0}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Field</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Phone</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {experts.map((expert) => (
              <TableRow
                key={expert.id}
                sx={{
                  transition: 'all 0.3s',
                  '&:hover': {
                    bgcolor: '#fff9f9',
                    boxShadow: 1
                  },
                  bgcolor: selectedExperts.includes(expert.id) ? '#ffebee' : 'transparent'
                }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedExperts.includes(expert.id)}
                    onChange={() => handleSelectExpert(expert.id)}
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {expert.photo && (
                      <img 
                        src={`https://bisauli.com/api3/${expert.photo}`}
                        alt={expert.name}
                        style={{ 
                          width: 40, 
                          height: 40, 
                          borderRadius: '50%', 
                          objectFit: 'cover',
                          border: '2px solid #ddd'
                        }}
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    )}
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {expert.name}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip label={expert.field} size="small" variant="outlined" />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {expert.phone1}
                    {expert.phone2 && ` / ${expert.phone2}`}
                  </Typography>
                </TableCell>
                <TableCell>
                  {expert.email || '-'}
                </TableCell>
                <TableCell align="center">
                  {expert.pending === 1 || expert.pending === '1' ? (
                    <Chip label="Pending" color="warning" size="small" />
                  ) : (
                    <Chip label="Active" color="success" size="small" />
                  )}
                </TableCell>
                <TableCell align="center">
                  <Button
                    size="small"
                    variant="contained"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDeleteClick(expert)}
                    disabled={loading}
                    sx={{
                      transition: 'all 0.3s',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: 3
                      }
                    }}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ✅ DELETE CONFIRMATION DIALOG */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setDeletingExpertId(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, color: '#d32f2f' }}>
          🗑️ Delete Expert(s)
        </DialogTitle>
        <DialogContent sx={{ py: 2 }}>
          {deletingExpertId ? (
            <Typography color="text.secondary">
              Are you sure you want to delete this expert? 
              <br /><br />
              The expert will be moved to trash and <strong>won't appear in  listings</strong>. 
              You can not access it .
            </Typography>
          ) : (
            <Typography color="text.secondary">
              Are you sure you want to delete <strong>{selectedExperts.length} expert(s)</strong>? 
              <br /><br />
              They will be moved to trash and <strong>won't appear in public listings</strong>. 
              You can still access them from trash if needed.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setDeletingExpertId(null);
            }}
            color="primary"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDelete}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
          >
            {loading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeleteExperts;